import { useEffect, useRef, useState } from 'react';
import { appCommands, appContext } from '../app/commands';
import type { MenuId } from '../core/commands/types';
import { shortcutsOf } from '../core/commands/types';
import { formatShortcut } from '../core/input/shortcuts';
import { useEditorStore } from '../state/editorStore';

const MENUS: { id: MenuId; label: string }[] = [
  { id: 'file', label: 'File' },
  { id: 'edit', label: 'Edit' },
  { id: 'view', label: 'View' },
  { id: 'image', label: 'Image' },
  { id: 'adjust', label: 'Adjust' },
  { id: 'layer', label: 'Layer' },
  { id: 'help', label: 'Help' },
];

/**
 * Menu bar, generated entirely from the command registry. Because commands own
 * their `menu`/`section`/`shortcut`/`isEnabled` metadata, adding a feature never
 * means touching this file.
 */
export function MenuBar() {
  const [open, setOpen] = useState<MenuId | null>(null);
  const root = useRef<HTMLDivElement>(null);

  // Subscribe to the state the enabled/checked predicates depend on.
  useEditorStore((s) => s.revision);
  useEditorStore((s) => s.canUndo);
  useEditorStore((s) => s.canRedo);
  useEditorStore((s) => s.showGrid);
  useEditorStore((s) => s.showRulers);
  useEditorStore((s) => s.rightPanelOpen);
  useEditorStore((s) => s.viewport.zoom);
  useEditorStore((s) => s.doc.id);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(null);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(null);
    };
    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const run = async (id: string, toggle?: boolean) => {
    const result = await appCommands.execute(id, appContext);
    if (!result.ok) appContext.notify(result.reason, 'warn');
    if (!toggle) setOpen(null);
  };

  return (
    <div
      ref={root}
      role="menubar"
      aria-label="Main menu"
      className="flex h-8 shrink-0 items-center gap-0.5 border-b border-line bg-surface px-1.5 select-none"
      data-testid="menubar"
    >
      <span className="px-2 text-[12px] font-semibold tracking-tight text-ink">
        Web<span className="text-accent">Paint</span>
      </span>
      {MENUS.map((menu) => {
        const items = appCommands.forMenu(menu.id, appContext);
        const groups = [...appCommands.groupBySection(items).entries()];
        return (
          <div key={menu.id} className="relative">
            <button
              type="button"
              role="menuitem"
              aria-haspopup="menu"
              aria-expanded={open === menu.id}
              onClick={() => setOpen(open === menu.id ? null : menu.id)}
              onMouseEnter={() => open && setOpen(menu.id)}
              className={`rounded px-2 py-1 ${
                open === menu.id ? 'bg-surface-3 text-ink' : 'text-muted hover:bg-surface-2'
              }`}
              data-testid={`menu-${menu.id}`}
            >
              {menu.label}
            </button>

            {open === menu.id ? (
              <div
                role="menu"
                className="absolute top-full left-0 z-40 min-w-56 rounded-lg border border-line bg-surface-2 py-1 shadow-2xl shadow-black/50"
                data-testid={`menu-panel-${menu.id}`}
              >
                {groups.length === 0 ? (
                  <p className="px-3 py-2 text-faint">Nothing here yet</p>
                ) : null}
                {groups.map(([section, group], groupIndex) => (
                  <div
                    key={section}
                    className={groupIndex > 0 ? 'mt-1 border-t border-line pt-1' : ''}
                  >
                    {group.map((command) => {
                      const enabled = appCommands.isEnabled(command, appContext);
                      const checked = appCommands.isChecked(command, appContext);
                      const reason = appCommands.disabledReason(command, appContext);
                      return (
                        <button
                          key={command.id}
                          type="button"
                          // A toggle is a menuitemcheckbox, not a plain menuitem:
                          // screen readers announce the state, which is the whole
                          // point of "Show Grid" having a tick.
                          {...(command.toggle
                            ? { role: 'menuitemcheckbox' as const, 'aria-checked': checked }
                            : { role: 'menuitem' as const })}
                          disabled={!enabled}
                          title={reason ?? command.description}
                          onClick={() => void run(command.id, command.toggle)}
                          className="flex w-full items-center gap-3 px-3 py-1.5 text-left enabled:hover:bg-accent-soft disabled:cursor-not-allowed disabled:text-faint"
                          data-testid={`menuitem-${command.id}`}
                        >
                          <span className="w-3.5 text-accent">
                            {command.toggle && checked ? '✓' : ''}
                          </span>
                          <span className="flex-1">{command.title}</span>
                          <span className="font-mono text-[11px] text-faint">
                            {shortcutsOf(command)[0]
                              ? formatShortcut(shortcutsOf(command)[0] as string)
                              : ''}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
